import React, { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import api from '../services/api'
import { toast } from 'sonner'
import { YoutubeLogo, LinkBreak, ArrowClockwise, Play } from '@phosphor-icons/react'

function formatNumber(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(n)
}

export default function YouTubePage() {
  const location = useLocation()
  const [status, setStatus] = useState(null) // null = loading
  const [videos, setVideos] = useState([])
  const [projects, setProjects] = useState([])
  const [loadingVideos, setLoadingVideos] = useState(false)
  const [connectingOAuth, setConnectingOAuth] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [linkingVideoId, setLinkingVideoId] = useState(null)

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/youtube/status')
      setStatus(data)
    } catch {
      setStatus({ connected: false })
    }
  }, [])

  const fetchVideos = useCallback(async () => {
    setLoadingVideos(true)
    try {
      const { data } = await api.get('/youtube/videos')
      setVideos(data.videos || [])
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar vídeos')
    } finally {
      setLoadingVideos(false)
    }
  }, [])

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/projects')
      setProjects(data || [])
    } catch {
      // non-critical
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    fetchProjects()
  }, [fetchStatus, fetchProjects])

  // Handle OAuth redirect callback
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('connected') === 'true') {
      toast.success('¡Canal de YouTube conectado!')
      window.history.replaceState({}, '', '/youtube')
      fetchStatus()
    }
  }, [location.search, fetchStatus])

  // Load videos once connected
  useEffect(() => {
    if (status?.connected) fetchVideos()
  }, [status?.connected, fetchVideos])

  const handleConnect = async () => {
    setConnectingOAuth(true)
    try {
      const { data } = await api.get('/youtube/oauth/start')
      window.location.href = data.auth_url
    } catch (err) {
      toast.error('No se pudo iniciar la conexión con YouTube')
      setConnectingOAuth(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar tu canal de YouTube?')) return
    setDisconnecting(true)
    try {
      await api.delete('/youtube/disconnect')
      setStatus({ connected: false })
      setVideos([])
      toast.success('Canal desconectado')
    } catch {
      toast.error('Error al desconectar')
    } finally {
      setDisconnecting(false)
    }
  }

  const handleLinkToProject = async (videoId, projectId) => {
    if (!projectId) return
    setLinkingVideoId(videoId)
    try {
      await api.post(`/api/projects/${projectId}/youtube-link`, {
        youtube_video_id: videoId,
      })
      toast.success('Vídeo vinculado al proyecto')
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al vincular')
    } finally {
      setLinkingVideoId(null)
    }
  }

  // ── Not connected ─────────────────────────────────────────────────────────
  if (status === null) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-ds-red border-t-transparent  animate-spin" />
      </div>
    )
  }

  if (!status.connected) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6 px-4">
        <div className="w-16 h-16  bg-[#FF0000]/10 flex items-center justify-center">
          <YoutubeLogo size={36} color="#FF0000" weight="fill" />
        </div>
        <div className="text-center">
          <h1 className="text-white text-xl font-semibold mb-2">Conecta tu canal de YouTube</h1>
          <p className="text-[#6E6E73] text-sm max-w-sm">
            Importa métricas reales de tus vídeos y vincúlalos a tus proyectos para analizar el
            rendimiento directamente desde la app.
          </p>
        </div>
        <button
          onClick={handleConnect}
          disabled={connectingOAuth}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#FF0000] hover:bg-[#CC0000] text-white  text-sm font-medium transition-colors disabled:opacity-60"
        >
          {connectingOAuth ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent  animate-spin" />
          ) : (
            <YoutubeLogo size={18} weight="fill" />
          )}
          Conectar con Google
        </button>
      </div>
    )
  }

  // ── Connected ─────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Channel card */}
      <div className="bg-ds-elevated border border-ds-border  p-4 flex items-center gap-4">
        {status.channel_thumbnail ? (
          <img
            src={status.channel_thumbnail}
            alt={status.channel_name}
            className="w-12 h-12  object-cover"
          />
        ) : (
          <div className="w-12 h-12  bg-[#FF0000]/20 flex items-center justify-center">
            <YoutubeLogo size={24} color="#FF0000" weight="fill" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{status.channel_name || 'Mi canal'}</p>
          <p className="text-[#6E6E73] text-xs">Canal conectado</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchVideos}
            disabled={loadingVideos}
            title="Actualizar lista"
            className="p-2 text-[#6E6E73] hover:text-white transition-colors"
          >
            <ArrowClockwise size={16} className={loadingVideos ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-ds-red border border-ds-red/30  hover:bg-[rgba(212,43,30,0.1)] transition-colors disabled:opacity-50"
          >
            <LinkBreak size={14} />
            Desconectar
          </button>
        </div>
      </div>

      {/* Videos list */}
      <div>
        <h2 className="text-white font-medium mb-3 text-sm">
          Tus vídeos{' '}
          {videos.length > 0 && <span className="text-[#6E6E73]">({videos.length})</span>}
        </h2>

        {loadingVideos ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-ds-red border-t-transparent  animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-[#6E6E73] text-sm text-center py-8">
            No se encontraron vídeos en este canal.
          </p>
        ) : (
          <div className="space-y-2">
            {videos.map(video => (
              <VideoRow
                key={video.video_id}
                video={video}
                projects={projects}
                onLink={handleLinkToProject}
                linking={linkingVideoId === video.video_id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function VideoRow({ video, projects, onLink, linking }) {
  const [selectedProject, setSelectedProject] = useState('')

  return (
    <div className="bg-ds-elevated border border-ds-border  p-3 flex items-center gap-3">
      {/* Thumbnail */}
      <div className="relative w-20 h-12  overflow-hidden flex-shrink-0 bg-ds-elevated">
        {video.thumbnail ? (
          <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play size={20} color="#6E6E73" weight="fill" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{video.title}</p>
        <p className="text-[#6E6E73] text-xs mt-0.5">
          {formatNumber(video.view_count)} views · {formatNumber(video.like_count)} likes
        </p>
      </div>

      {/* Link to project */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <select
          value={selectedProject}
          onChange={e => setSelectedProject(e.target.value)}
          className="bg-ds-elevated border border-ds-border text-[#A1A1A6] text-xs  px-2 py-1.5 focus:outline-none focus:border-ds-red/50 max-w-[140px]"
        >
          <option value="">Vincular a proyecto…</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </select>
        <button
          onClick={() => onLink(video.video_id, selectedProject)}
          disabled={!selectedProject || linking}
          className="px-2.5 py-1.5 bg-[rgba(212,43,30,0.1)] hover:bg-[rgba(212,43,30,0.2)] text-ds-red text-xs  transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {linking ? '…' : 'Vincular'}
        </button>
      </div>
    </div>
  )
}
